/**
 * useUnreadCounts Hook
 * 
 * Manages unread message counts for all conversations.
 * Features:
 * - Real-time subscription to unread counts
 * - Increment count when new message arrives
 * - Clear count when conversation is opened
 * - Calculate total unread across all conversations
 */

import { useState, useEffect, useCallback } from 'react';
import {
  subscribeToUnreadCounts,
  clearUnreadCount,
  incrementUnreadCount,
  getUnreadCounts
} from '@/lib/firestore';

export function useUnreadCounts(userId) {
  const [unreadCounts, setUnreadCounts] = useState({});
  const [loading, setLoading] = useState(true);

  // Subscribe to real-time unread count updates
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    const unsubscribe = subscribeToUnreadCounts(userId, (counts) => {
      setUnreadCounts(counts || {});
      setLoading(false);
    });

    return () => unsubscribe();
  }, [userId]);

  // Clear unread count for a conversation
  const markAsRead = useCallback(async (conversationId) => {
    if (!userId || !conversationId) return;

    try {
      await clearUnreadCount(userId, conversationId);
      // Optimistically update local state
      setUnreadCounts(prev => {
        const updated = { ...prev };
        delete updated[conversationId];
        return updated;
      });
    } catch (err) {
      console.error('Error clearing unread count:', err);
    }
  }, [userId]);

  // Increment unread count for a conversation
  const incrementUnread = useCallback(async (conversationId) => {
    if (!userId || !conversationId) return;

    try {
      await incrementUnreadCount(userId, conversationId);
    } catch (err) {
      console.error('Error incrementing unread count:', err);
    }
  }, [userId]);

  // Get unread count for a specific conversation
  const getUnreadCount = useCallback((conversationId) => {
    return unreadCounts[conversationId] || 0;
  }, [unreadCounts]);

  // Calculate total unread across all conversations
  const totalUnread = Object.values(unreadCounts).reduce((sum, count) => sum + count, 0);

  return {
    unreadCounts,
    totalUnread,
    loading,
    markAsRead,
    incrementUnread,
    getUnreadCount
  };
}
